import { mockSectionData, type MockSectionCustomerInput } from './mockSectionData.util';
import type { Cadence } from './reportEnums.constants';
import {
  buildYearMoRangeEndingAt,
  buildYearQtrRangeEndingAt,
  formatYearMoToReportMonthLabel,
  formatYearQtrToReportLabel,
  yearMoToYearQtr,
} from './reportYearMo.util';
import { WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE } from './whoWereReachingSnapshotDetect.util';
import { coerceWhoWereReachingMemberCount } from './whoWereReachingSnapshotDetect.util';

/** Static copy for the four-step "how a need gets met" journey (not warehouse data). */
export interface WhatMembersNeedFulfillmentStep {
  step: number;
  title: string;
  body: string;
}

/** Fixed steps shown in the What Members Need section preview. */
export const WHAT_MEMBERS_NEED_FULFILLMENT_STEPS: WhatMembersNeedFulfillmentStep[] =
  [
    {
      step: 1,
      title: 'We find it',
      body: 'Peer conversation surfaces the need.',
    },
    {
      step: 2,
      title: 'We know where to go',
      body: 'Reema matches to a vetted resource.',
    },
    {
      step: 3,
      title: 'We try again if we have to',
      body: 'Warm handoff to the provider or program.',
    },
    {
      step: 4,
      title: "The member tells us it's met",
      body: 'Follow-up confirms the need was met.',
    },
  ];

/** Number of trend points for identified vs fulfilled in what-members-need. */
export const WHAT_MEMBERS_NEED_TREND_POINT_COUNT = 4;

/** Max ranked need issues shown in "What members need most". */
export const WHAT_MEMBERS_NEED_TOP_COUNT = 5;

/** Ranked need row for the what-members-need bar chart. */
export interface WhatMembersNeedTopNeedRow {
  label: string;
  count: number;
  pct?: number;
}

/** Identified vs fulfilled trend point for what-members-need. */
export interface WhatMembersNeedIdentifiedVsFulfilledRow {
  label: string;
  identified: number;
  fulfilled: number;
}

/** Fulfillment summary stats shown above the charts. */
export interface WhatMembersNeedFulfillmentStats {
  monthlyFulfillmentPct: number;
  monthlyFulfilledCount: number;
  monthlyIdentifiedCount: number;
  totalFulfillmentPct: number;
  totalFulfilledCount: number;
  totalIdentifiedCount: number;
}

/**
 * Subcopy under the “What members need most” chart title.
 * @param label - Top-ranked need label.
 * @param pct - Share of displayed needs (0–100).
 * @returns Studio line (e.g. "Bills & expenses dominate at 35% of all needs").
 */
export const buildWhatMembersNeedTopNeedsSubcopy = (
  label: string,
  pct: number,
): string => `${label} dominate at ${pct}% of all needs`;

/**
 * Subcopy under the total needs-fulfilled stat card.
 * @param fulfilled - Cumulative needs fulfilled across the trend window.
 * @param identified - Cumulative needs identified across the trend window.
 * @returns Studio line (e.g. "1,248 of 2,615 needs identified").
 */
export const buildWhatMembersNeedTotalFulfillmentSubcopy = (
  fulfilled: number,
  identified: number,
): string =>
  `${fulfilled.toLocaleString('en-US')} of ${identified.toLocaleString('en-US')} needs identified`;

const toCount = (value: unknown): number =>
  coerceWhoWereReachingMemberCount(value);

const normalizeYearMoKey = (yearMo: number | string): string =>
  String(yearMo).trim().padStart(6, '0');

/**
 * Builds ranked need rows from `member_month_need` aggregates by `need_issue`.
 * @param rows - Top need rows from the warehouse.
 * @returns Sorted need rows with share percentages, limited to the top {@link WHAT_MEMBERS_NEED_TOP_COUNT}.
 */
export const buildWhatMembersNeedTopNeedsFromIssueRows = (
  rows: Array<{ need_issue?: unknown; identified_count?: unknown }>,
): WhatMembersNeedTopNeedRow[] => {
  const parsed = rows
    .map(row => {
      const label =
        typeof row.need_issue === 'string' ? row.need_issue.trim() : '';
      const count = toCount(row.identified_count);
      if (!label || count <= 0) {
        return null;
      }
      return { label, count };
    })
    .filter((row): row is { label: string; count: number } => row !== null)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, WHAT_MEMBERS_NEED_TOP_COUNT);

  const total = parsed.reduce((sum, row) => sum + row.count, 0);
  if (total <= 0) {
    return [];
  }

  return parsed.map(row => ({
    ...row,
    pct: Math.round((row.count / total) * 100),
  }));
};

/**
 * Builds identified vs fulfilled trend points from `member_month_need` aggregates.
 * @param rows - Trend rows from the warehouse (any order).
 * @param periodKeys - Ordered period keys (`YYYYMM` or `YYYYQ#`).
 * @param cadence - Report cadence for label formatting.
 * @returns Trend rows aligned to `periodKeys`.
 */
export const buildWhatMembersNeedTrendFromMemberMonthNeed = (
  rows: Array<{
    period_key?: unknown;
    identified?: unknown;
    fulfilled?: unknown;
  }>,
  periodKeys: string[],
  cadence: Cadence,
): WhatMembersNeedIdentifiedVsFulfilledRow[] => {
  const byPeriod = new Map(
    rows.map(row => [
      String(row.period_key ?? '').trim(),
      {
        identified: toCount(row.identified),
        fulfilled: toCount(row.fulfilled),
      },
    ]),
  );

  return periodKeys.map(periodKey => {
    const counts = byPeriod.get(periodKey) ?? { identified: 0, fulfilled: 0 };
    const label =
      cadence === 'quarterly'
        ? (formatYearQtrToReportLabel(periodKey) ?? periodKey)
        : (formatYearMoToReportMonthLabel(periodKey) ?? periodKey);
    return {
      label,
      identified: counts.identified,
      fulfilled: counts.fulfilled,
    };
  });
};

/**
 * Computes monthly and cumulative fulfillment stats from trend rows.
 * @param currentCounts - Counts for the report end month.
 * @param trend - Identified vs fulfilled trend (typically four months).
 * @returns Fulfillment stat block for the section header.
 */
export const buildWhatMembersNeedFulfillmentStats = (
  currentCounts: { identified: number; fulfilled: number },
  trend: WhatMembersNeedIdentifiedVsFulfilledRow[],
): WhatMembersNeedFulfillmentStats => {
  const totalIdentified = trend.reduce((sum, row) => sum + row.identified, 0);
  const totalFulfilled = trend.reduce((sum, row) => sum + row.fulfilled, 0);

  const monthlyIdentified = currentCounts.identified;
  const monthlyFulfilled = currentCounts.fulfilled;

  return {
    monthlyIdentifiedCount: monthlyIdentified,
    monthlyFulfilledCount: monthlyFulfilled,
    monthlyFulfillmentPct:
      monthlyIdentified > 0
        ? Math.round((monthlyFulfilled / monthlyIdentified) * 100)
        : 0,
    totalIdentifiedCount: totalIdentified,
    totalFulfilledCount: totalFulfilled,
    totalFulfillmentPct:
      totalIdentified > 0
        ? Math.round((totalFulfilled / totalIdentified) * 100)
        : 0,
  };
};

/**
 * Returns true when warehouse what-members-need data has at least one chart point.
 * @param topNeeds - Ranked need rows.
 * @param trend - Identified vs fulfilled trend.
 * @returns Whether the snapshot should be treated as warehouse-backed.
 */
export const whatMembersNeedHasWarehouseData = (
  topNeeds: WhatMembersNeedTopNeedRow[],
  trend: WhatMembersNeedIdentifiedVsFulfilledRow[],
): boolean =>
  topNeeds.length > 0 ||
  trend.some(row => row.identified > 0 || row.fulfilled > 0);

/**
 * Coerces ranked need rows from snapshot JSON.
 * @param raw - `topNeeds` array from snapshot.
 * @returns Valid need rows, limited to the top {@link WHAT_MEMBERS_NEED_TOP_COUNT} by count.
 */
export const coerceWhatMembersNeedTopNeeds = (
  raw: unknown,
): WhatMembersNeedTopNeedRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as { label?: unknown; count?: unknown; pct?: unknown };
      const label = typeof record.label === 'string' ? record.label.trim() : '';
      const count = toCount(record.count);
      if (!label || count <= 0) {
        return null;
      }
      const pct = toCount(record.pct);
      return {
        label,
        count,
        ...(pct > 0 ? { pct } : {}),
      };
    })
    .filter((row): row is WhatMembersNeedTopNeedRow => row !== null)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, WHAT_MEMBERS_NEED_TOP_COUNT);
};

/**
 * Coerces identified vs fulfilled trend rows from snapshot JSON.
 * @param raw - `identifiedVsFulfilled` array from snapshot.
 * @returns Valid trend rows.
 */
export const coerceWhatMembersNeedIdentifiedVsFulfilled = (
  raw: unknown,
): WhatMembersNeedIdentifiedVsFulfilledRow[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map(row => {
      if (!row || typeof row !== 'object') {
        return null;
      }
      const record = row as {
        label?: unknown;
        identified?: unknown;
        fulfilled?: unknown;
      };
      const label = typeof record.label === 'string' ? record.label.trim() : '';
      if (!label) {
        return null;
      }
      return {
        label,
        identified: toCount(record.identified),
        fulfilled: toCount(record.fulfilled),
      };
    })
    .filter(
      (row): row is WhatMembersNeedIdentifiedVsFulfilledRow => row !== null,
    );
};

/**
 * Coerces fulfillment stats from snapshot JSON.
 * @param raw - `fulfillmentStats` object from snapshot.
 * @returns Parsed stats or null when missing.
 */
export const coerceWhatMembersNeedFulfillmentStats = (
  raw: unknown,
): WhatMembersNeedFulfillmentStats | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const record = raw as Record<string, unknown>;
  return {
    monthlyFulfillmentPct: toCount(record.monthlyFulfillmentPct),
    monthlyFulfilledCount: toCount(record.monthlyFulfilledCount),
    monthlyIdentifiedCount: toCount(record.monthlyIdentifiedCount),
    totalFulfillmentPct: toCount(record.totalFulfillmentPct),
    totalFulfilledCount: toCount(record.totalFulfilledCount),
    totalIdentifiedCount: toCount(record.totalIdentifiedCount),
  };
};

/**
 * Builds fulfillment stat cards from an identified vs fulfilled trend.
 * @param trend - Trend rows (mock or warehouse).
 * @returns Fulfillment stats or null when the trend is empty.
 */
export const buildWhatMembersNeedFulfillmentStatsFromTrend = (
  trend: WhatMembersNeedIdentifiedVsFulfilledRow[],
): WhatMembersNeedFulfillmentStats | null => {
  if (trend.length === 0) {
    return null;
  }
  const current = trend[trend.length - 1];
  return buildWhatMembersNeedFulfillmentStats(
    { identified: current.identified, fulfilled: current.fulfilled },
    trend,
  );
};

/**
 * Returns true when fulfillment stats contain any non-zero counts.
 * @param stats - Fulfillment stats object.
 * @returns Whether warehouse fulfillment stats should be used.
 */
export const whatMembersNeedFulfillmentStatsHasData = (
  stats: WhatMembersNeedFulfillmentStats | null,
): boolean =>
  Boolean(
    stats &&
      (stats.monthlyIdentifiedCount > 0 ||
        stats.monthlyFulfilledCount > 0 ||
        stats.totalIdentifiedCount > 0 ||
        stats.totalFulfilledCount > 0),
  );

/**
 * Returns true when the identified vs fulfilled trend has meaningful counts.
 * @param trend - Trend rows.
 * @returns Whether warehouse trend data should be used.
 */
export const whatMembersNeedTrendHasData = (
  trend: WhatMembersNeedIdentifiedVsFulfilledRow[],
): boolean => trend.some(row => row.identified > 0 || row.fulfilled > 0);

/**
 * Builds the what-members-need snapshot from `member_month_need` warehouse aggregates.
 * @param topNeedRows - Ranked need issues for the report end period.
 * @param trendRows - Identified vs fulfilled counts by period.
 * @param yearMo - Six-digit period key for the report end month.
 * @param cadence - Report cadence.
 * @param customer - Report customer context.
 * @param reportingPeriod - Human-readable report period label.
 * @returns Snapshot object or null when warehouse data is empty.
 */
export const buildWhatMembersNeedSnapshotFromMemberMonthNeed = (
  topNeedRows: Array<{ need_issue?: unknown; identified_count?: unknown }>,
  trendRows: Array<{
    period_key?: unknown;
    identified?: unknown;
    fulfilled?: unknown;
  }>,
  yearMo: string,
  cadence: Cadence,
  customer: MockSectionCustomerInput,
  reportingPeriod: string,
): Record<string, unknown> | null => {
  const topNeeds = buildWhatMembersNeedTopNeedsFromIssueRows(topNeedRows);
  const periodKeys =
    cadence === 'quarterly'
      ? (buildYearQtrRangeEndingAt(
          yearMo,
          WHAT_MEMBERS_NEED_TREND_POINT_COUNT,
        ) ?? [])
      : (buildYearMoRangeEndingAt(
          yearMo,
          WHAT_MEMBERS_NEED_TREND_POINT_COUNT,
        ) ?? []);
  const identifiedVsFulfilled = buildWhatMembersNeedTrendFromMemberMonthNeed(
    trendRows,
    periodKeys,
    cadence,
  );

  if (!whatMembersNeedHasWarehouseData(topNeeds, identifiedVsFulfilled)) {
    return null;
  }

  const currentPeriodKey =
    cadence === 'quarterly' ? yearMoToYearQtr(yearMo) : normalizeYearMoKey(yearMo);
  const currentIndex = periodKeys.findIndex(key => key === currentPeriodKey);
  const currentTrendRow =
    currentIndex >= 0
      ? identifiedVsFulfilled[currentIndex]
      : identifiedVsFulfilled[identifiedVsFulfilled.length - 1];
  const fulfillmentStats = buildWhatMembersNeedFulfillmentStats(
    {
      identified: currentTrendRow?.identified ?? 0,
      fulfilled: currentTrendRow?.fulfilled ?? 0,
    },
    identifiedVsFulfilled,
  );

  const mockBase = mockSectionData(
    'what-members-need',
    customer,
    reportingPeriod,
  );
  const parsedYearMo = Number.parseInt(yearMo, 10);

  return {
    ...mockBase,
    snapshotSource: WHO_WERE_REACHING_WAREHOUSE_SNAPSHOT_SOURCE,
    yearMo: Number.isFinite(parsedYearMo) ? parsedYearMo : yearMo,
    topNeeds,
    identifiedVsFulfilled,
    fulfillmentStats,
  };
};
